import { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Grid,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Divider,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Button,
} from '@chakra-ui/react';
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import { CHANNEL_LABELS, CHANNEL_COLORS } from '../constants/sourceChannels';
import { APILeadSourceEntry, APIOverviewTrendPoint } from 'types/api.types';
import {
  LeadInsights,
  timeToCall,
  type LeadDetail,
  type LeadEntity,
} from './LeadInsights';

// ── Type definitions ──────────────────────────────────────────────────────────

interface SourceBreakdown {
  leads: number;
  nb: number;
  won: number;
  archived?: number;
  close_rate: number;
}

interface Sources {
  call_list: SourceBreakdown;
  direct: SourceBreakdown;
  organic: SourceBreakdown;
  mychoice?: SourceBreakdown;
  other: SourceBreakdown;
}

interface TodayCounts {
  total_leads: number;
  won_quotes: number;
  archived_quotes?: number;
  actual_total?: number;
  nb_total?: number;
}

interface BrokerLeadRow {
  label: string;
  email: string;
  branch_id: number;
  score: number;
  quotes: number;
  won: number;
  archived: number;
  closing_ratio: number;
  sources: Sources;
  lead_source_breakdown?: Record<string, LeadSourceChannelEntry>;
  today: TodayCounts;
  detail: LeadDetail;
}

type LeadSourceChannelEntry = APILeadSourceEntry;

interface BranchLeadRow {
  label: string;
  branch_id: number;
  score: number;
  quotes: number;
  won: number;
  archived: number;
  closing_ratio: number;
  sources: Sources;
  lead_source_breakdown?: Record<string, LeadSourceChannelEntry>;
  today: TodayCounts;
  detail: LeadDetail;
  brokers: BrokerLeadRow[];
}

interface AdminManagerData {
  type: 'admin' | 'manager';
  summary: LeadEntity;
  branches: BranchLeadRow[];
  aggregate_lead_source?: Record<string, LeadSourceChannelEntry>;
}

interface BrokerTodayBySource {
  call_list: { leads: number; won: number; nb: number };
  direct: { leads: number; won: number; nb: number };
  organic: { leads: number; won: number; nb: number };
}

interface BrokerTodayDetail {
  date: string | null;
  total_leads: number;
  won_quotes: number;
  archived: number;
  nb_total: number;
  by_source: BrokerTodayBySource;
}

interface BrokerData {
  type: 'broker';
  monthly: BrokerLeadRow;
  today: BrokerTodayDetail;
}

type LeadManagementData = AdminManagerData | BrokerData;

interface Props {
  data: LeadManagementData | null | undefined;
  isLoading?: boolean;
  selectedBranchId?: number | null;
  selectedBrokerEmail?: string | null;
  trends: APIOverviewTrendPoint[];
  onBranchClick?: (branchId: number) => void;
  onBrokerClick?: (email: string) => void;
}

// ── Formatters ────────────────────────────────────────────────────────────────

function fmtN(v: number | undefined | null, decimals = 0): string {
  if (v == null) return '—';
  return v.toLocaleString('en-CA', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function fmtCurrency(v: number | null | undefined): string {
  if (v == null || v === 0) return '$0';
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

function fmtPct(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${(v * 100).toFixed(0)}%`;
}

// ── Lead source channel breakdown (vw_LeadSource_Metrics) ────────────────────

function ChannelBreakdownTable({
  breakdown,
}: {
  breakdown: Record<string, LeadSourceChannelEntry>;
}) {
  const hdrBg = useColorModeValue('gray.50', 'navy.700');
  const hoverBg = useColorModeValue('gray.50', 'navy.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const entries = Object.entries(breakdown)
    .filter(([, v]) => v.leads > 0)
    .sort((a, b) => b[1].leads - a[1].leads);

  if (!entries.length)
    return (
      <Text fontSize="xs" color="gray.400" py="8px">
        No lead source data for this period
      </Text>
    );

  const totalLeads = entries.reduce((s, [, v]) => s + v.leads, 0);

  return (
    <Box
      borderRadius="8px"
      overflow="hidden"
      border="1px solid"
      borderColor={borderColor}
    >
      <Table size="xs" variant="simple">
        <Thead bg={hdrBg}>
          <Tr>
            <Th fontSize="10px" py="7px" pl="12px">
              Source
            </Th>
            <Th fontSize="10px" py="7px" textAlign="center">
              Leads
            </Th>
            <Th fontSize="10px" py="7px" textAlign="center">
              New
            </Th>
            <Th fontSize="10px" py="7px" textAlign="center">
              In Progress
            </Th>
            <Th fontSize="10px" py="7px" textAlign="center">
              Won
            </Th>
            <Th fontSize="10px" py="7px" textAlign="center">
              Lost
            </Th>
            <Th fontSize="10px" py="7px" textAlign="right">
              NB Premium
            </Th>
            <Th fontSize="10px" py="7px" textAlign="right">
              Closing %
            </Th>
            <Th fontSize="10px" py="7px" textAlign="right" pr="12px">
              Time to Call
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {entries.map(([slug, s]) => (
            <Tr key={slug} _hover={{ bg: hoverBg }}>
              <Td py="8px" pl="12px">
                <Flex align="center" gap="6px">
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="2px"
                    bg={CHANNEL_COLORS[slug] ?? 'gray.400'}
                    flexShrink={0}
                  />
                  <Text fontSize="xs" fontWeight="600">
                    {CHANNEL_LABELS[slug] ?? slug}
                  </Text>
                  <Text fontSize="9px" color="gray.400">
                    {Math.round((s.leads / (totalLeads || 1)) * 100)}%
                  </Text>
                </Flex>
              </Td>
              <Td fontSize="xs" textAlign="center" fontWeight="600">
                {fmtN(s.leads)}
              </Td>
              <Td fontSize="xs" textAlign="center">
                {fmtN(s.new)}
              </Td>
              <Td fontSize="xs" textAlign="center">
                {fmtN(s.in_progress)}
              </Td>
              <Td
                fontSize="xs"
                textAlign="center"
                color="green.600"
                fontWeight="600"
              >
                {fmtN(s.won)}
              </Td>
              <Td
                fontSize="xs"
                textAlign="center"
                color="red.400"
                fontWeight="600"
              >
                {fmtN(s.lost)}
              </Td>
              <Td
                fontSize="xs"
                textAlign="right"
                color="green.700"
                fontWeight="700"
              >
                {s.nb > 0 ? (
                  fmtCurrency(s.nb)
                ) : (
                  <Text as="span" color="gray.300" fontWeight="400">
                    —
                  </Text>
                )}
              </Td>
              <Td
                fontSize="xs"
                textAlign="right"
                pr="4px"
                color={
                  s.close_rate >= 0.2
                    ? 'green.600'
                    : s.close_rate >= 0.15
                      ? 'orange.500'
                      : 'red.500'
                }
                fontWeight="700"
              >
                {s.close_rate > 0 ? `${(s.close_rate * 100).toFixed(1)}%` : '—'}
              </Td>
              <Td fontSize="xs" textAlign="right" pr="12px">
                {timeToCall(s.ttc_hours)}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

// ── Fallback: old source breakdown (AHT-based, used when lead_source_breakdown absent) ──

function SourceBreakdownTable({ sources }: { sources: Sources }) {
  const hdrBg = useColorModeValue('gray.50', 'navy.700');
  const totalNB =
    (sources.call_list.nb ?? 0) +
    (sources.direct.nb ?? 0) +
    (sources.organic.nb ?? 0) +
    (sources.mychoice?.nb ?? 0) +
    (sources.other?.nb ?? 0);

  const rows = [
    { label: 'Call List', src: sources.call_list, color: 'orange.500' },
    { label: 'Direct', src: sources.direct, color: 'teal.500' },
    { label: 'Organic', src: sources.organic, color: 'cyan.600' },
    ...((sources.mychoice?.leads ?? 0)
      ? [{ label: 'MyChoice', src: sources.mychoice!, color: 'pink.500' }]
      : []),
    ...(sources.other?.leads > 0
      ? [{ label: 'Other', src: sources.other, color: 'gray.400' }]
      : []),
  ];

  return (
    <Box
      borderRadius="8px"
      overflow="hidden"
      border="1px solid"
      borderColor="gray.200"
    >
      <Table size="xs" variant="simple">
        <Thead bg={hdrBg}>
          <Tr>
            <Th fontSize="10px" py="7px" pl="12px">
              Source
            </Th>
            <Th fontSize="10px" py="7px" textAlign="center">
              Monthly Leads
            </Th>
            <Th fontSize="10px" py="7px" textAlign="right" pr="12px">
              NB Premium
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map(({ label, src, color }) => (
            <Tr key={label} _hover={{ bg: 'gray.50' }}>
              <Td py="8px" pl="12px">
                <Flex align="center" gap="6px">
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg={color}
                    flexShrink={0}
                  />
                  <Text fontSize="xs" fontWeight="600">
                    {label}
                  </Text>
                </Flex>
              </Td>
              <Td fontSize="xs" textAlign="center" fontWeight="600">
                {src.leads > 0 ? (
                  <Flex justify="center" align="center" gap="6px">
                    <Text>{fmtN(src.leads)}</Text>
                    {totalNB > 0 && (
                      <Text fontSize="9px" color="gray.400">
                        (
                        {Math.round(
                          (src.leads /
                            (rows.reduce((s, r) => s + r.src.leads, 0) || 1)) *
                            100,
                        )}
                        %)
                      </Text>
                    )}
                  </Flex>
                ) : (
                  <Text color="gray.300">0</Text>
                )}
              </Td>
              <Td
                fontSize="xs"
                textAlign="right"
                pr="12px"
                color="green.700"
                fontWeight="700"
              >
                {src.nb > 0 ? (
                  fmtCurrency(src.nb)
                ) : (
                  <Text as="span" color="gray.300" fontWeight="400">
                    —
                  </Text>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

// ── Branch row ────────────────────────────────────────────────────────────────

function BranchRow({
  row,
  onBranchClick,
  onBrokerClick,
}: {
  row: BranchLeadRow;
  onBranchClick?: (branchId: number) => void;
  onBrokerClick?: (email: string) => void;
}) {
  const bg = useColorModeValue('white', 'navy.800');
  const panelBg = useColorModeValue('gray.50', 'navy.750');
  const border = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'navy.700');
  const labelColor = useColorModeValue('gray.500', 'gray.400');

  const totalNB =
    Object.keys(row.lead_source_breakdown ?? {}).length > 0
      ? Object.values(row.lead_source_breakdown!).reduce(
          (s, v) => s + (v.nb ?? 0),
          0,
        )
      : (row.sources.call_list.nb ?? 0) +
        (row.sources.direct.nb ?? 0) +
        (row.sources.organic.nb ?? 0) +
        (row.sources.mychoice?.nb ?? 0);

  return (
    <AccordionItem
      border="1px solid"
      borderColor={border}
      borderRadius="12px"
      mb="8px"
      overflow="hidden"
    >
      {/* ── Header ── */}
      <AccordionButton
        onClick={() => onBranchClick?.(row.branch_id)}
        bg={bg}
        _hover={{ bg: hoverBg }}
        px="20px"
        py="14px"
        _expanded={{ borderBottom: '1px solid', borderColor: border }}
      >
        <Flex
          flex={1}
          align="center"
          gap="16px"
          flexWrap="wrap"
          textAlign="left"
        >
          {/* Branch name */}
          <Box minW="160px" flex="1">
            <Text
              fontWeight="700"
              fontSize="sm"
              color="gray.800"
              lineHeight="1.3"
            >
              {row.label}
            </Text>
            {row.brokers.length > 0 && (
              <Text fontSize="xs" color="gray.400" mt="1px">
                {row.brokers.length} broker{row.brokers.length !== 1 ? 's' : ''}
              </Text>
            )}
          </Box>

          {/* Monthly stats */}
          <Flex gap="24px" align="center" flexShrink={0}>
            <Box textAlign="center" minW="44px">
              <Text
                fontSize="10px"
                color={labelColor}
                fontWeight="600"
                textTransform="uppercase"
              >
                Leads
              </Text>
              <Text fontSize="md" fontWeight="800" color="gray.800">
                {fmtN(row.quotes)}
              </Text>
            </Box>
            <Box textAlign="center" minW="44px">
              <Text
                fontSize="10px"
                color={labelColor}
                fontWeight="600"
                textTransform="uppercase"
              >
                Won
              </Text>
              <Text fontSize="md" fontWeight="800" color="green.600">
                {fmtN(row.won)}
              </Text>
            </Box>
            <Box textAlign="center" minW="56px">
              <Text
                fontSize="10px"
                color={labelColor}
                fontWeight="600"
                textTransform="uppercase"
              >
                NB Prem
              </Text>
              <Text fontSize="md" fontWeight="800" color="blue.700">
                {fmtCurrency(totalNB)}
              </Text>
            </Box>
            <Box textAlign="center" minW="52px">
              <Text
                fontSize="10px"
                color={labelColor}
                fontWeight="600"
                textTransform="uppercase"
              >
                Closing
              </Text>
              <Text fontSize="md" fontWeight="800" color="purple.600">
                {fmtPct(row.closing_ratio)}
              </Text>
            </Box>
          </Flex>
        </Flex>
        <AccordionIcon ml="12px" color="gray.400" />
      </AccordionButton>

      {/* ── Expanded panel ── */}
      <AccordionPanel bg={panelBg} px="20px" py="16px">
        <Grid
          templateColumns={
            row.brokers.length > 0 ? { base: '1fr', md: '1fr 1fr' } : '1fr'
          }
          gap="16px"
        >
          {/* Source breakdown — prefer new vw_LeadSource_Metrics channel data */}
          <Box>
            <Text
              fontSize="xs"
              fontWeight="700"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="0.5px"
              mb="8px"
            >
              Lead Sources — Monthly
            </Text>
            {row.lead_source_breakdown &&
            Object.keys(row.lead_source_breakdown).length > 0 ? (
              <ChannelBreakdownTable breakdown={row.lead_source_breakdown} />
            ) : (
              <SourceBreakdownTable sources={row.sources} />
            )}
          </Box>

          {row.brokers.length > 0 && (
            <Box>
              <Text
                fontSize="xs"
                fontWeight="700"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="0.5px"
                mb="8px"
              >
                Brokers in Branch
              </Text>
              <Box
                borderRadius="8px"
                overflow="hidden"
                border="1px solid"
                borderColor="gray.200"
              >
                <Table size="xs" variant="simple">
                  <Thead bg="gray.100">
                    <Tr>
                      <Th fontSize="10px" py="7px" pl="12px">
                        Broker
                      </Th>
                      <Th fontSize="10px" py="7px" textAlign="center">
                        Leads
                      </Th>
                      <Th fontSize="10px" py="7px" textAlign="center">
                        Won
                      </Th>
                      <Th fontSize="10px" py="7px" textAlign="right" pr="12px">
                        Today
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {row.brokers.map((broker) => (
                      <Tr
                        key={broker.email}
                        _hover={{ bg: 'white' }}
                        cursor={onBrokerClick ? 'pointer' : undefined}
                        onClick={() => onBrokerClick?.(broker.email)}
                      >
                        <Td pl="12px" py="7px">
                          <Text fontSize="xs" fontWeight="600" color="gray.800">
                            {broker.label}
                          </Text>
                          <Text fontSize="9px" color="gray.400">
                            {broker.email}
                          </Text>
                        </Td>
                        <Td fontSize="xs" textAlign="center" fontWeight="600">
                          {fmtN(broker.quotes)}
                        </Td>
                        <Td
                          fontSize="xs"
                          textAlign="center"
                          color="green.600"
                          fontWeight="600"
                        >
                          {fmtN(broker.won)}
                        </Td>
                        <Td pr="12px" textAlign="right">
                          {(broker.today?.total_leads ?? 0) > 0 ? (
                            <Badge colorScheme="blue" fontSize="9px">
                              {fmtN(broker.today.total_leads)} leads
                            </Badge>
                          ) : (
                            <Text fontSize="9px" color="gray.300">
                              —
                            </Text>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          )}
        </Grid>
      </AccordionPanel>
    </AccordionItem>
  );
}

// ── Broker self-view ──────────────────────────────────────────────────────────

function BrokerView({
  data,
  trends,
}: {
  data: BrokerData;
  trends: APIOverviewTrendPoint[];
}) {
  const bg = useColorModeValue('white', 'navy.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const { monthly, today } = data;

  return (
    <Box>
      <LeadInsights
        entity={monthly}
        sources={monthly.lead_source_breakdown ?? {}}
        trends={trends}
      />
      <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap="16px">
        <Box
          bg={bg}
          border="1px solid"
          borderColor={border}
          borderRadius="12px"
          p="20px"
        >
          <Text fontWeight="700" fontSize="md" color="gray.800" mb="16px">
            Lead Sources — Monthly
          </Text>
          {monthly?.lead_source_breakdown &&
          Object.keys(monthly.lead_source_breakdown).length > 0 ? (
            <ChannelBreakdownTable breakdown={monthly.lead_source_breakdown} />
          ) : (
            monthly?.sources && (
              <SourceBreakdownTable sources={monthly.sources} />
            )
          )}
        </Box>

        {/* Today's live activity */}
        <Box
          bg={bg}
          border="1px solid"
          borderColor={border}
          borderRadius="12px"
          p="20px"
        >
          <Flex align="center" justify="space-between" mb="16px">
            <Text fontWeight="700" fontSize="md" color="gray.800">
              Today's Activity
            </Text>
            {today?.date && (
              <Badge colorScheme="green" variant="subtle" fontSize="xs">
                Live
              </Badge>
            )}
          </Flex>
          <Grid templateColumns="repeat(2, 1fr)" gap="12px" mb="16px">
            <Box textAlign="center" bg="blue.50" borderRadius="10px" p="16px">
              <Text fontSize="xs" color="blue.600" fontWeight="700" mb="4px">
                LEADS
              </Text>
              <Text fontSize="3xl" fontWeight="800" color="blue.700">
                {fmtN(today?.total_leads ?? 0)}
              </Text>
            </Box>
            <Box textAlign="center" bg="green.50" borderRadius="10px" p="16px">
              <Text fontSize="xs" color="green.600" fontWeight="700" mb="4px">
                WON
              </Text>
              <Text fontSize="3xl" fontWeight="800" color="green.700">
                {fmtN(today?.won_quotes ?? 0)}
              </Text>
            </Box>
          </Grid>
          <Divider mb="12px" />
          <Text
            fontSize="xs"
            fontWeight="700"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="0.5px"
            mb="10px"
          >
            By Source — Today
          </Text>
          <Grid templateColumns="repeat(3, 1fr)" gap="8px">
            {(['call_list', 'direct', 'organic'] as const).map((src) => {
              const s = today?.by_source?.[src] ?? { leads: 0, won: 0, nb: 0 };
              const labels: Record<string, string> = {
                call_list: 'Call List',
                direct: 'Direct',
                organic: 'Organic',
              };
              return (
                <Box
                  key={src}
                  bg="gray.50"
                  borderRadius="8px"
                  p="10px"
                  textAlign="center"
                >
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    fontWeight="600"
                    mb="6px"
                  >
                    {labels[src]}
                  </Text>
                  <Text fontSize="sm" fontWeight="800" color="gray.800">
                    {fmtN(s.leads)}
                  </Text>
                  <Text fontSize="10px" color="gray.400">
                    leads
                  </Text>
                  {s.nb > 0 && (
                    <Text
                      fontSize="10px"
                      color="green.600"
                      fontWeight="600"
                      mt="2px"
                    >
                      {fmtCurrency(s.nb)}
                    </Text>
                  )}
                </Box>
              );
            })}
          </Grid>
        </Box>
      </Grid>
    </Box>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

type BranchSortKey = 'score' | 'quotes' | 'won' | 'closing_ratio';
const BRANCH_SORT_LABELS: Record<BranchSortKey, string> = {
  score: 'Score',
  quotes: 'Leads',
  won: 'Won',
  closing_ratio: 'Closing %',
};

export const LeadManagementPanel = ({
  data,
  isLoading,
  selectedBranchId,
  selectedBrokerEmail,
  trends,
  onBranchClick,
  onBrokerClick,
}: Props) => {
  const bg = useColorModeValue('white', 'navy.800');
  const border = useColorModeValue('gray.200', 'gray.700');

  const adminManagerData =
    data && data.type !== 'broker' ? (data as AdminManagerData) : null;
  const [sortKey, setSortKey] = useState<BranchSortKey>('quotes');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  function toggleSort(key: BranchSortKey) {
    if (key === sortKey) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  const branches = useMemo(() => {
    if (!adminManagerData) return [];
    let rows = [...adminManagerData.branches];

    // Apply branch filter
    if (selectedBranchId) {
      rows = rows.filter((b) => b.branch_id === selectedBranchId);
    }

    // Apply broker filter — narrow each branch's brokers list
    if (selectedBrokerEmail) {
      rows = rows
        .map((b) => ({
          ...b,
          brokers: (b.brokers ?? []).filter(
            (br) => br.email === selectedBrokerEmail,
          ),
        }))
        .filter((b) => (b.brokers ?? []).length > 0);
    }

    rows.sort((a, b) => {
      const va = ((a as never)[sortKey] as number) ?? 0;
      const vb = ((b as never)[sortKey] as number) ?? 0;
      return sortDir === 'desc' ? vb - va : va - vb;
    });

    return rows;
  }, [
    adminManagerData,
    selectedBranchId,
    selectedBrokerEmail,
    sortKey,
    sortDir,
  ]);

  if (isLoading) {
    return (
      <Box
        bg={bg}
        border="1px solid"
        borderColor={border}
        borderRadius="12px"
        p="40px"
        textAlign="center"
      >
        <Text color="gray.400">Loading lead management data…</Text>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box
        bg={bg}
        border="1px solid"
        borderColor={border}
        borderRadius="12px"
        p="40px"
        textAlign="center"
      >
        <Text color="gray.400">
          No lead management data available for this period
        </Text>
      </Box>
    );
  }

  if (data.type === 'broker') {
    return <BrokerView data={data} trends={trends} />;
  }

  const isFiltered = !!(selectedBranchId || selectedBrokerEmail);
  const scoped = selectedBrokerEmail
    ? branches
        .flatMap((b) => b.brokers)
        .find((br) => br.email === selectedBrokerEmail)
    : branches.find((b) => b.branch_id === selectedBranchId);
  const entity: LeadEntity | undefined = isFiltered ? scoped : data.summary;
  const displayLeadSource =
    (isFiltered ? scoped?.lead_source_breakdown : data.aggregate_lead_source) ??
    {};
  const hasLeadSource = Object.keys(displayLeadSource).length > 0;
  const activityHeading = `${scoped?.label ?? (data.type === 'admin' ? 'All Branches' : 'Your Branches')} — Lead Activity`;

  return (
    <Box>
      {entity ? (
        <LeadInsights
          entity={entity}
          sources={displayLeadSource}
          trends={trends}
        />
      ) : (
        <Text color="gray.400" mb="20px">
          No lead data for this selection
        </Text>
      )}

      {hasLeadSource && (
        <Box mb="20px">
          <Text fontWeight="700" fontSize="sm" color="gray.700" mb="12px">
            Lead Source Breakdown
          </Text>
          <ChannelBreakdownTable breakdown={displayLeadSource} />
        </Box>
      )}

      <Flex
        align="center"
        justify="space-between"
        mb="10px"
        flexWrap="wrap"
        gap="8px"
      >
        <Text fontWeight="700" fontSize="md" color="gray.700">
          {activityHeading}
        </Text>
        <Text fontSize="xs" color="gray.400">
          {branches.length} branch{branches.length !== 1 ? 'es' : ''} · click to
          expand
        </Text>
      </Flex>

      <Flex gap="6px" mb="12px" align="center" flexWrap="wrap">
        <Text fontSize="xs" color="gray.500" mr="2px">
          Sort by:
        </Text>
        {(Object.keys(BRANCH_SORT_LABELS) as BranchSortKey[]).map((key) => (
          <Button
            key={key}
            size="xs"
            variant={sortKey === key ? 'solid' : 'outline'}
            colorScheme={sortKey === key ? 'brand' : 'gray'}
            onClick={() => toggleSort(key)}
            fontSize="xs"
            rightIcon={
              sortKey === key ? (
                sortDir === 'desc' ? (
                  <TriangleDownIcon boxSize="8px" />
                ) : (
                  <TriangleUpIcon boxSize="8px" />
                )
              ) : undefined
            }
          >
            {BRANCH_SORT_LABELS[key]}
          </Button>
        ))}
      </Flex>

      <Accordion
        allowMultiple
        key={`${selectedBranchId}-${selectedBrokerEmail}`}
        defaultIndex={isFiltered ? [0] : []}
      >
        {branches.map((branch) => (
          <BranchRow
            key={branch.branch_id}
            row={branch}
            onBranchClick={onBranchClick}
            onBrokerClick={onBrokerClick}
          />
        ))}
      </Accordion>
    </Box>
  );
};
